import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Play, ShoppingCart, Heart, Star, CheckCircle, Download, Share2, 
  Clock, FileText, Globe, Award, TrendingUp, ChevronDown, ChevronUp 
} from 'lucide-react';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSection, setExpandedSection] = useState({});

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchProductDetails();
    fetchRelatedProducts();
  }, [productId]);

  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/marketplace/products/details/${productId}`);
      const data = await response.json();
      setProduct(data);
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedProducts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/marketplace/browse`);
      const data = await response.json();
      setRelatedProducts(data.slice(0, 4));
    } catch (error) {
      console.error('Error fetching related products:', error);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Product Not Found</h2>
          <button 
            onClick={() => navigate('/marketplace')}
            className="text-purple-600 hover:text-purple-700"
          >
            Return to Marketplace
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumbs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <button onClick={() => navigate('/marketplace')} className="hover:text-purple-600">
              Marketplace
            </button>
            <span>/</span>
            <span className="text-gray-900 font-medium">{product.title}</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Left 2/3 */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero Media */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="relative h-96 bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                {(product.product_type === 'video' || product.product_type === 'course') && (
                  <button className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-xl hover:scale-110 transition-transform">
                    <Play className="w-10 h-10 text-purple-600 ml-1" />
                  </button>
                )}
                {product.preview_url && (
                  <button className="absolute top-4 right-4 px-4 py-2 bg-white text-purple-600 rounded-lg shadow-lg hover:bg-purple-50 transition-colors font-medium">
                    <Play className="w-4 h-4 inline mr-2" />
                    Preview
                  </button>
                )}
              </div>
            </div>

            {/* Tabs Navigation */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="border-b border-gray-200 px-6">
                <div className="flex space-x-8">
                  {['overview', 'storytelling', 'reviews'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === tab
                          ? 'border-purple-600 text-purple-600'
                          : 'border-transparent text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}\n                </div>
              </div>

              <div className="p-6">
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    {/* Overview */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Overview</h3>
                      <p className="text-gray-700 leading-relaxed\">{product.description}</p>
                    </div>

                    {/* Highlights */}
                    {product.story && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Why This Matters</h3>
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                          <p className="text-gray-700 leading-relaxed italic\">{product.story}</p>
                        </div>
                      </div>
                    )}

                    {/* Details */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Details</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {product.duration && (
                          <div className="flex items-center space-x-2">
                            <Clock className="w-5 h-5 text-gray-400" />
                            <div>
                              <p className="text-xs text-gray-500\">Duration</p>
                              <p className="text-sm font-medium text-gray-900\">{formatDuration(product.duration)}</p>
                            </div>
                          </div>
                        )}
                        {product.pages && (
                          <div className="flex items-center space-x-2">
                            <FileText className="w-5 h-5 text-gray-400" />
                            <div>
                              <p className="text-xs text-gray-500\">Pages</p>
                              <p className="text-sm font-medium text-gray-900\">{product.pages} pages</p>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center space-x-2">
                          <Globe className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-xs text-gray-500\">Language</p>
                            <p className="text-sm font-medium text-gray-900\">English</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Download className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-xs text-gray-500\">Format</p>
                            <p className="text-sm font-medium text-gray-900 uppercase\">{product.format}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'storytelling' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Creator's Intention</h3>
                      <p className="text-gray-700 leading-relaxed\">{product.creator_intention}</p>
                    </div>

                    {/* Video Embed Placeholder */}
                    <div className="relative h-64 bg-gray-900 rounded-lg flex items-center justify-center">
                      <Play className="w-16 h-16 text-white opacity-80" />
                      <p className="absolute bottom-4 left-4 text-white text-sm\">Storytelling Video</p>
                    </div>

                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6">
                      <p className="text-gray-700 italic\">
                        \"This product was created with the intention of bringing peace, clarity, and 
                        transformation to those who experience it. Every element has been crafted with 
                        consciousness and purpose.\"
                      </p>
                    </div>
                  </div>
                )}

                {activeTab === 'reviews' && (
                  <div className="space-y-6">
                    {/* Rating Summary */}
                    <div className="flex items-start space-x-8">
                      <div className="text-center">
                        <div className="text-5xl font-bold text-gray-900 mb-2\">
                          {product.average_resonance.toFixed(1)}
                        </div>
                        <div className="flex items-center justify-center mb-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star 
                              key={star}
                              className={`w-5 h-5 ${
                                star <= Math.round(product.average_resonance)
                                  ? 'text-yellow-500 fill-current'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <p className="text-sm text-gray-600\">{product.total_reflections} reflections</p>
                      </div>

                      <div className="flex-1 space-y-2">
                        {[5, 4, 3, 2, 1].map((rating) => (
                          <div key={rating} className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600 w-12\">{rating} star</span>
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-yellow-500"
                                style={{ width: `${rating === 5 ? 60 : rating === 4 ? 20 : 10}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-600 w-12 text-right\">
                              {rating === 5 ? 60 : rating === 4 ? 20 : 10}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Individual Reviews */}
                    <div className="space-y-4 pt-6 border-t border-gray-200">
                      {[1, 2, 3].map((review) => (
                        <div key={review} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="flex items-center space-x-2 mb-1">
                                <div className="w-8 h-8 bg-purple-200 rounded-full\"></div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900\">Anonymous User</p>
                                  <p className="text-xs text-gray-500\">2 weeks ago</p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-1\">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star key={star} className="w-4 h-4 text-yellow-500 fill-current\" />
                                ))}
                              </div>
                            </div>
                          </div>
                          <p className="text-gray-700 text-sm mb-2\">
                            This content has been transformational for our organization. 
                            The depth and quality exceeded our expectations.
                          </p>
                          <div className="flex flex-wrap gap-2\">
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium\">
                              Focused
                            </span>
                            <span className="px-2 py-1 bg-pink-100 text-pink-700 rounded text-xs font-medium\">
                              Transformative
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <button className="w-full py-3 border-2 border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors font-medium\">
                      Write a Reflection
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar - Right 1/3 */}
          <div className="space-y-6">
            {/* Purchase Card */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 sticky top-6\">
              <div className="mb-4\">
                <div className="flex items-baseline space-x-2 mb-2\">
                  <span className="text-3xl font-bold text-gray-900\">${product.price.toFixed(2)}</span>
                  <span className="text-sm text-gray-500\">{product.currency}</span>
                </div>
                <p className="text-sm text-green-600 font-medium\">Lifetime access</p>
              </div>

              <div className="space-y-3 mb-6\">
                <button className="w-full py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold flex items-center justify-center space-x-2\">
                  <ShoppingCart className="w-5 h-5\" />
                  <span>Buy Now</span>
                </button>
                <button className="w-full py-3 border-2 border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors font-semibold\">
                  Add to Cart
                </button>
                <button className="w-full py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center justify-center space-x-2\">
                  <Heart className="w-5 h-5\" />
                  <span>Add to Wishlist</span>
                </button>
              </div>

              <div className="space-y-3 pt-6 border-t border-gray-200\">
                <div className="flex items-center justify-between text-sm\">
                  <span className="text-gray-600\">Rating</span>
                  <div className="flex items-center space-x-1\">
                    <Star className="w-4 h-4 text-yellow-500 fill-current\" />
                    <span className="font-medium\">{product.average_resonance.toFixed(1)}</span>
                    <span className="text-gray-500\">({product.total_reflections})</span>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm\">
                  <span className="text-gray-600\">Purchases</span>
                  <span className="font-medium\">{product.total_sales.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-sm\">
                  <span className="text-gray-600\">Views</span>
                  <span className="font-medium\">{product.views_count.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Seller Card */}
            <div className="bg-white rounded-lg border border-gray-200 p-6\">
              <div className="flex items-center space-x-3 mb-4\">
                <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center\">
                  <Award className="w-6 h-6 text-purple-600\" />
                </div>
                <div className="flex-1\">
                  <div className="flex items-center space-x-2\">
                    <h3 className="font-semibold text-gray-900\">Certified Seller</h3>
                    <CheckCircle className="w-4 h-4 text-purple-600\" />
                  </div>
                  <p className="text-sm text-gray-600\">Conscious Organization</p>
                </div>
              </div>
              <button className="w-full py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium\">
                View Store
              </button>
            </div>

            {/* Share */}
            <div className="bg-white rounded-lg border border-gray-200 p-6\">
              <button className="w-full flex items-center justify-center space-x-2 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium\">
                <Share2 className="w-5 h-5\" />
                <span>Share</span>
              </button>
            </div>
          </div>
        </div>

        {/* Related Products */}
        <div className="mt-16\">
          <h2 className="text-2xl font-bold text-gray-900 mb-6\">Recommended Products</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6\">
            {relatedProducts.map((related) => (
              <div
                key={related.product_id}
                onClick={() => navigate(`/marketplace/product/${related.product_id}`)}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg hover:border-purple-300 transition-all cursor-pointer\">
                <div className="h-40 bg-gradient-to-br from-purple-100 to-pink-100\"></div>
                <div className="p-4\">
                  <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2\">{related.title}</h3>
                  <div className="flex items-center justify-between\">
                    <span className="text-lg font-bold text-gray-900\">${related.price.toFixed(2)}</span>
                    <div className="flex items-center space-x-1\">
                      <Star className="w-4 h-4 text-yellow-500 fill-current\" />
                      <span className="text-sm font-medium\">{related.average_resonance.toFixed(1)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
